import React, { useState, useRef } from 'react';
import { ChevronLeft, ChevronRight, Camera, LogOut, Trash2, Mail, CreditCard } from 'lucide-react';
import ApiService from '../services/ApiService';

function ProfileView({ user, families = [], relatives = [], onBack, onLogout, onUserUpdated, onShowToast, onSelectFamily, onOpenSubscription }) {
  const [name, setName] = useState(user.name || '');
  const [birthYear, setBirthYear] = useState(user.birthDate?.birthYear?.toString() || '');
  const [birthMonth, setBirthMonth] = useState(user.birthDate?.birthMonth?.toString() || '');
  const [birthDay, setBirthDay] = useState(user.birthDate?.birthDay?.toString() || '');
  const [timezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [profileImagePreview, setProfileImagePreview] = useState(
    user.profilePictureUrl || null
  );
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const fileInputRef = useRef(null);

  const handleProfilePictureChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Preview
    const reader = new FileReader();
    reader.onload = (ev) => setProfileImagePreview(ev.target.result);
    reader.readAsDataURL(file);

    // Upload
    const formData = new FormData();
    formData.append('profilePicture', file);

    setSaving(true);
    try {
      await ApiService.uploadProfilePicture(formData);
      onShowToast?.('Profile picture updated', 'success');
      if (onUserUpdated) onUserUpdated();
    } catch (error) {
      onShowToast?.('Failed to upload picture', 'error');
    }
    setSaving(false);
  };

  const handleSaveName = async () => {
    if (!name.trim() || !user.uuid) return;
    setSaving(true);
    try {
      await ApiService.updateUserProperties({
        action: 'update',
        userUpdates: [{ uuid: user.uuid, name: name.trim() }]
      });
      onShowToast?.('Name updated', 'success');
      if (onUserUpdated) onUserUpdated();
    } catch (error) {
      onShowToast?.(error.message || 'Failed to update name', 'error');
    }
    setSaving(false);
  };

  const handleSaveBirthday = async () => {
    if (!birthYear || !birthMonth || !birthDay) return;
    setSaving(true);
    try {
      await ApiService.updateUserProperties({
        action: 'update',
        userUpdates: [{
          uuid: user.uuid,
          birthDate: {
            birthYear: parseInt(birthYear),
            birthMonth: parseInt(birthMonth),
            birthDay: parseInt(birthDay)
          }
        }]
      });
      onShowToast?.('Birthday updated', 'success');
      if (onUserUpdated) onUserUpdated();
    } catch (error) {
      onShowToast?.(error.message || 'Failed to update birthday', 'error');
    }
    setSaving(false);
  };

  const handleDeleteAccount = async () => {
    setSaving(true);
    try {
      await ApiService.deleteUser();
      onLogout();
    } catch (error) {
      onShowToast?.('Failed to delete account', 'error');
      setSaving(false);
    }
  };

  const getInitial = (n) => n ? n[0].toUpperCase() : '?';

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 100 }, (_, i) => currentYear - i);
  const months = Array.from({ length: 12 }, (_, i) => i + 1);
  const days = Array.from({ length: 31 }, (_, i) => i + 1);

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Header */}
      <div className="bg-gray-800 p-4 flex items-center gap-3">
        <button onClick={onBack} className="p-2 hover:bg-gray-700 rounded-lg transition-colors">
          <ChevronLeft className="w-6 h-6" />
        </button>
        <h1 className="text-lg font-bold">Profile</h1>
      </div>

      <div className="p-4 space-y-6">
        {/* Profile Photo */}
        <div className="flex flex-col items-center">
          <div
            className="relative cursor-pointer group"
            onClick={() => fileInputRef.current?.click()}
          >
            {profileImagePreview ? (
              <img src={profileImagePreview} alt="Profile" className="w-28 h-28 rounded-full object-cover" />
            ) : (
              <div className="w-28 h-28 rounded-full bg-gray-700 flex items-center justify-center">
                <span className="text-4xl font-semibold text-gray-400">{getInitial(name)}</span>
              </div>
            )}
            <div className="absolute inset-0 bg-black/40 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
              <Camera className="w-8 h-8 text-white" />
            </div>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleProfilePictureChange}
            className="hidden"
          />
          <p className="text-xs text-gray-500 mt-2">Tap to change photo</p>
        </div>

        {/* Name */}
        <div className="bg-gray-800 rounded-xl p-4">
          <label className="text-sm text-gray-400 mb-2 block">Name</label>
          <div className="flex gap-2">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="flex-1 bg-gray-700 text-white px-3 py-2 rounded-lg focus:outline-none focus:ring-1 focus:ring-purple-500"
            />
            <button
              onClick={handleSaveName}
              disabled={saving || !name.trim()}
              className="px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-sm font-medium disabled:opacity-50 transition-colors"
            >
              Save
            </button>
          </div>
        </div>

        {/* Birthday */}
        <div className="bg-gray-800 rounded-xl p-4">
          <label className="text-sm text-gray-400 mb-2 block">Birthday</label>
          <div className="flex gap-2">
            <select
              value={birthMonth}
              onChange={(e) => setBirthMonth(e.target.value)}
              className="flex-1 bg-gray-700 text-white px-3 py-2 rounded-lg focus:outline-none focus:ring-1 focus:ring-purple-500"
            >
              <option value="">Month</option>
              {months.map(m => (
                <option key={m} value={m}>
                  {new Date(2000, m - 1).toLocaleString('default', { month: 'long' })}
                </option>
              ))}
            </select>
            <select
              value={birthDay}
              onChange={(e) => setBirthDay(e.target.value)}
              className="w-20 bg-gray-700 text-white px-3 py-2 rounded-lg focus:outline-none focus:ring-1 focus:ring-purple-500"
            >
              <option value="">Day</option>
              {days.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
            <select
              value={birthYear}
              onChange={(e) => setBirthYear(e.target.value)}
              className="w-24 bg-gray-700 text-white px-3 py-2 rounded-lg focus:outline-none focus:ring-1 focus:ring-purple-500"
            >
              <option value="">Year</option>
              {years.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          {birthYear && birthMonth && birthDay && (
            <button
              onClick={handleSaveBirthday}
              disabled={saving}
              className="mt-3 w-full py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-sm font-medium disabled:opacity-50 transition-colors"
            >
              Save Birthday
            </button>
          )}
        </div>

        {/* Timezone */}
        <div className="bg-gray-800 rounded-xl p-4">
          <label className="text-sm text-gray-400 mb-1 block">Timezone</label>
          <p className="text-white text-sm">{timezone}</p>
        </div>

        {/* Family Groups */}
        {families.length > 0 && (
          <div className="bg-gray-800 rounded-xl p-4">
            <label className="text-sm text-gray-400 mb-2 block">Family Groups</label>
            <div className="space-y-2">
              {families.map(family => (
                <div
                  key={family.uuid}
                  onClick={() => onSelectFamily?.(family)}
                  className="flex items-center justify-between py-2 border-b border-gray-700 last:border-0 cursor-pointer hover:bg-gray-700/50 -mx-1 px-1 rounded transition-colors"
                >
                  <div>
                    <p className="text-white text-sm font-medium">{family.name}</p>
                    <p className="text-xs text-gray-500">{family.role} {family.memberCount ? `\u00B7 ${family.memberCount} members` : ''}</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-500" />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Subscription */}
        <div className="bg-gray-800 rounded-xl p-4">
          <label className="text-sm text-gray-400 mb-2 block">Subscription</label>
          <button
            onClick={onOpenSubscription}
            className="flex items-center justify-between w-full py-2 cursor-pointer hover:bg-gray-700/50 -mx-1 px-1 rounded transition-colors"
          >
            <div className="flex items-center gap-2">
              <CreditCard className="w-4 h-4 text-purple-400" />
              <span className="text-white text-sm font-medium">Manage Subscription</span>
            </div>
            <ChevronRight className="w-4 h-4 text-gray-500" />
          </button>
        </div>

        {/* Support */}
        <div className="space-y-3 pt-4">
          <a
            href="mailto:support@membox.app"
            className="flex items-center justify-center gap-2 w-full py-3 bg-gray-800 hover:bg-gray-700 rounded-xl text-white text-sm font-medium transition-colors"
          >
            <Mail className="w-4 h-4" />
            Contact Support
          </a>

          <button
            onClick={onLogout}
            className="flex items-center justify-center gap-2 w-full py-3 bg-gray-800 hover:bg-gray-700 rounded-xl text-white text-sm font-medium transition-colors"
          >
            <LogOut className="w-4 h-4" />
            Sign Out
          </button>

          {!showDeleteConfirm ? (
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="flex items-center justify-center gap-2 w-full py-3 bg-gray-800 hover:bg-red-900/30 rounded-xl text-red-400 text-sm font-medium transition-colors"
            >
              <Trash2 className="w-4 h-4" />
              Delete Account
            </button>
          ) : (
            <div className="bg-red-900/20 border border-red-500/30 rounded-xl p-4">
              <p className="text-sm text-red-300 mb-3">Are you sure? This action cannot be undone.</p>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="flex-1 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-sm text-white transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteAccount}
                  disabled={saving}
                  className="flex-1 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-sm text-white font-medium disabled:opacity-50 transition-colors"
                >
                  {saving ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ProfileView;
